import { useMemo, type ReactNode } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { LumanityProvider } from "@lumanity/ui";
import { getEnv } from "@/config/env";
import { useAuth } from "@/contexts/AuthContext";

const API_BASE_URL = getEnv("VITE_API_BASE_URL") || "http://localhost:8000";

/**
 * Wires @lumanity/ui to FRaiME: same token as the rest of the app, FRaiME's
 * own QueryClient (so the library's queries show up in the same cache), and
 * the service URLs FRaiME already uses.
 *
 * - content-intelligence calls go through FRaiME's backend (`/api/intelligence/*`)
 * - project-service calls go through its pass-through (`/api/project-service`)
 */
export function FraimeLumanityProvider({ queryClient, children }: { queryClient: QueryClient; children: ReactNode }) {
  const { getAccessToken } = useAuth();
  const config = useMemo(
    () => ({
      apiBaseUrl: API_BASE_URL,
      dataIngestionApiUrl: getEnv("VITE_DATA_INGESTION_API_URL") || API_BASE_URL,
      evidenceBaseApiUrl: getEnv("VITE_EVIDENCE_BASE_API_URL") || API_BASE_URL,
      contentIntelligenceApiUrl: API_BASE_URL,
      projectsApiUrl: `${API_BASE_URL}/api/project-service`,
      getAccessToken,
    }),
    [getAccessToken],
  );
  return (
    <LumanityProvider config={config} queryClient={queryClient}>
      {children}
    </LumanityProvider>
  );
}
